import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

// ═══════════════════════════════════════════════════════════════
// TYPES — Files & Folders
// ═══════════════════════════════════════════════════════════════

export interface ESignFileResponse {
  id: string
  name: string
  folderId?: string
  folderName?: string
  uploadedBy: string
  uploadedDate: string
  fileSize: number
  mimeType?: string
}

export interface ESignFolderResponse {
  id: string
  name: string
  parentId?: string
}

export interface ESignFilesListResponse {
  files: ESignFileResponse[]
  totalFiles: number
}

export interface ESignFoldersListResponse {
  folders: ESignFolderResponse[]
}

// ═══════════════════════════════════════════════════════════════
// TYPES — Templates
// ═══════════════════════════════════════════════════════════════

export interface SaveTemplatePayload {
  fileId: string
  fields: Record<string, unknown>[]
  signingOrder: Record<string, unknown>[]
  isSequential?: boolean
}

export interface TemplateResponse {
  id: string
  fileId: string
  fields: Record<string, unknown>[]
  signingOrder: Record<string, unknown>[]
  isSequential?: boolean
  createdBy: string
  createdDate: string
  modifiedDate: string
}

// ═══════════════════════════════════════════════════════════════
// TYPES — Candidates & Sites
// Candidates are applicants from the applicant service, scoped
// to the current agency user's agencyId on the BE.
// ═══════════════════════════════════════════════════════════════

export interface ESignCandidateResult {
  // References the Applicant._id from the applicant service
  id: string
  firstName: string
  lastName: string
  email: string
  siteId?: string
  siteName?: string
  departmentName?: string
}

export type PersonType = 'candidate' | 'agency-user'

export interface ESignPersonResult extends ESignCandidateResult {
  personType: PersonType
}

export interface ESignSiteResult {
  id: string
  companyName: string
  regionName?: string
  siteName: string
  candidateCount: number
}

export interface AllCandidatesResponse {
  candidates: ESignCandidateResult[]
  totalCount: number
}

// ═══════════════════════════════════════════════════════════════
// TYPES — Signature Requests
// ═══════════════════════════════════════════════════════════════

export interface SendSignatureRequestBody {
  templateId: string
  fileId: string
  // recipientIds are Applicant._id values from the applicant service
  recipientIds: string[]
  signingOrder: Record<string, unknown>[]
  isSequential?: boolean
  message?: string
  audience?: {
    mode: string
    groupFilters?: unknown[]
    excludedIds?: string[]
    specificIds?: string[]
    // STFFLG-3868 (Phase 2b): per-assignment fan-out for "By Specific Person".
    // Mirrors the msp-service AudiencePayload DTO exactly. Optional and sent
    // ALONGSIDE specificIds (kept for backward compatibility). When present the
    // backend treats it as authoritative for specific-people mode and ignores
    // specificIds. Assignment ids are Assignment DOCUMENT _ids, NOT the human
    // EA000001 keys.
    selectedRecipients?: {applicantId: string; assignmentIds?: string[]}[]
  }
  // Active agency from the agency-switcher (multi-agency users). Injected
  // automatically by sendSignatureRequest so send is scoped to the same
  // branch agency used for candidate search. Backend validates it.
  agencyId?: string
}

export type SignatureRequestStatus = 'in_progress' | 'completed' | 'cancelled'

export interface SignatureRequestListItem {
  id: string
  fileName: string
  fileId: string
  totalRecipients: number
  completedCount: number
  pendingCount: number
  status: SignatureRequestStatus
  sentBy: string
  sentDate: string
  lastReminderDate?: string
  // Per-assignment eSign (SHR-3993 / STFFLG-3868). A request is scoped to a
  // (person, assignment) pair, so the BE snapshots the assignment label and the
  // recipient name onto the request at SEND time and never re-resolves them —
  // re-resolving would let a later title change silently rewrite an already
  // signed legal document.
  // Both are OPTIONAL and absent on every request sent before per-assignment
  // sending existed (there is no migration), so the UI must fall back to an
  // em-dash for those rows indefinitely.
  assignmentLabel?: string
  recipientName?: string
  /**
   * STFFLG-4163: a signer whose turn has come could not be notified and has not signed (see
   * RequestParticipant.notificationFailed). Absent on requests sent before the field existed,
   * which read as nothing needing attention.
   */
  needsAttention?: boolean
}

export interface SignatureRequestsListResponse {
  requests: SignatureRequestListItem[]
  totalRequests: number
}

// ═══════════════════════════════════════════════════════════════
// TYPES — EOR (Employer of Record) signer
//
// STFFLG-4163: the 'eor' signer is the admin email of an EOR agency, resolved PER DOCUMENT by
// the backend (ESignService.resolveDocumentEor) from the candidate's agency only: the agency on
// the document's assignment (Assignment.agencyId), else (no assignment, or an assignment with no
// agency) the candidate's own agency (Applicant.agencyId). That agency is then walked to its EOR:
// itself when isEor, else its parent. There is no fallback to the sender's agency or the
// company's: a document whose candidate's agency has no EOR is not sent, and is named in
// skippedRecipients. The same resolution gives the EOR name printed on the document, so the EOR
// named on a document is always the EOR asked to sign it. That printed name is meant to be the
// EOR's legal name, but the backend reads agencies through AgencyProfileResponse, which does not
// expose eorLegalName, so in practice it is the EOR agency's operational name (since STFFLG-3868).
// ═══════════════════════════════════════════════════════════════

/**
 * GET /esign/eor/resolve: the EOR of the active agency, which is the EOR for this agency's own
 * candidates (those whose assignment, or with no assignment whose own record, belongs to it).
 */
export interface ResolvedEorResponse {
  id: string
  /** The EOR agency's name: what the EOR signer row shows. */
  name: string
  /** Who the EOR slot emails. '' when the EOR agency has no admin email on file. */
  adminEmail: string
  /**
   * The EOR name printed on the document (in practice the operational name: see above). Absent
   * from a backend older than STFFLG-4163.
   */
  eorName?: string
}

/** One document's EOR, as a send would resolve it (POST /esign/eor/preview). */
export interface EorPreviewItem {
  /**
   * Where it was resolved from: the agency on the candidate's assignment, or the candidate's own
   * agency. Absent when there was no agency to resolve from (a document with no candidate).
   */
  source?: 'assignment' | 'candidate'
  /** The candidate's agency the EOR was resolved from. */
  sourceAgencyId?: string
  eorAgencyId?: string
  /** The EOR agency's name. */
  name?: string
  /** The EOR name printed on the document (in practice the operational name: see above). */
  eorName?: string
  /** Who the EOR slot would email. Absent when there is none. */
  adminEmail?: string
  /**
   * Set when a send with an EOR slot would SKIP this document, and why: the same text the send
   * reports in skippedRecipients after "no EOR signer: ", naming the candidate's agency.
   */
  error?: string
}

/** One document the send would create. */
export interface EorPreviewDocument extends EorPreviewItem {
  /**
   * The candidate the document is for. For a signing order with no Candidate slot: its
   * candidate signer, when there is exactly one; absent otherwise.
   */
  applicantId?: string
  /** Assignment DOCUMENT id the document would be pinned to. Absent for an unscoped document. */
  assignmentId?: string
  /** "EA000001 - Forklift Operator", telling apart one person's two documents. */
  assignmentLabel?: string
  /**
   * The candidate's name. For a signing order with no Candidate slot, the names of the candidates
   * who sign it, '' when it has none.
   */
  recipientName: string
}

/**
 * STFFLG-4163 response shape: one row per document. There is no `sender` item (no document takes
 * the sender's agency's EOR), and the rows are `documents`, which also covers the single document
 * of a signing order with no Candidate slot.
 */
export interface EorPreviewResponse {
  /**
   * One row per document the send would create, in every Send To mode; people the send would not
   * reach are left out. A row with `error` is a document the send would skip.
   */
  documents: EorPreviewDocument[]
}

/** The part of a send payload the EOR preview reads. */
export interface EorPreviewRequestBody {
  recipientIds?: string[]
  audience?: SendSignatureRequestBody['audience']
  agencyId?: string
  /**
   * The send's signing order, read only to tell whether it has a Candidate slot: without one the
   * send builds ONE document from the signing order, whose candidate is a specific-person signer
   * who is a candidate. Omitted, the documents are taken to have a Candidate slot.
   */
  signingOrder?: {type: string; specificPersonId?: string}[]
}

/**
 * The server's own message for a failed request (NestJS puts it on `response.data.message`, as a
 * string or a list of validation strings), else the error's message, else the fallback.
 */
export function esignErrorMessage(error: unknown, fallback: string): string {
  const data = (error as {response?: {data?: {message?: unknown}}} | null)?.response?.data
  const message = data?.message
  if (typeof message === 'string' && message.trim()) return message
  if (Array.isArray(message) && message.length > 0) return message.join(' ')
  if (error instanceof Error && error.message) return error.message
  return fallback
}

// ═══════════════════════════════════════════════════════════════
// TYPES — Request Detail & Participants
// ═══════════════════════════════════════════════════════════════

export type ParticipantStatus = 'waiting' | 'sent' | 'viewed' | 'signed' | 'cancelled'

export interface RequestParticipant {
  id: string
  // References the Applicant._id from the applicant service
  applicantId: string
  firstName: string
  lastName: string
  email: string
  participantType: string
  order: number
  status: ParticipantStatus
  sentDate?: string
  viewedDate?: string
  signedDate?: string
  cancelledDate?: string
  lastReminderDate?: string
  // STFFLG-3906 — audit trail behind the User Activity timeline. The dates above
  // describe a participant's current state; these describe how it got there.
  // All optional: requests created before this shipped have none of them, and the
  // timeline falls back to the single-date fields for those.
  /** When ESIGN Act consent was accepted (`consentGiven` alone carries no date). */
  consentDate?: string
  /** One entry per reminder sent; `lastReminderDate` is only the most recent. */
  reminderHistory?: string[]
  /** One entry per open, re-opens included; `viewedDate` is the first open only. */
  viewHistory?: string[]
  /**
   * STFFLG-4163: the EOR agency this participant signs for. Set on the 'eor' participant, and on
   * a candidate or specific-person participant whose email is also the EOR's (the backend merges
   * the two slots into one participant, which then holds the EOR's fields too).
   */
  eorAgencyId?: string
  /**
   * STFFLG-4163: the last attempt to tell this signer it is their turn did not reach them (the
   * email was rejected, or, where emails are off, the in-app notification failed). Such a signer
   * stays 'waiting' with no sentDate. A reminder that gets through clears it.
   */
  notificationFailed?: boolean
  /** Why, for the sender (e.g. the mail provider's error). Never shown to the signer. */
  notificationFailedReason?: string
  /** ISO time of the failed attempt. */
  notificationFailedAt?: string
}

export interface SignatureRequestDetail {
  id: string
  fileName: string
  fileId: string
  status: SignatureRequestStatus
  sentBy: string
  sentDate: string
  message?: string
  totalRecipients: number
  completedCount: number
  pendingCount: number
  participants: RequestParticipant[]
  // Assignment the request was sent for. Request-level (not per participant):
  // the DOCUMENT is what is assignment-scoped — co-signers (EOR, managers,
  // specific-person) are not. Optional / absent on pre-per-assignment requests.
  assignmentLabel?: string
  /** STFFLG-4163: see SignatureRequestListItem.needsAttention. */
  needsAttention?: boolean
  /**
   * STFFLG-4163 (D1): the EOR this document was sent under, the EOR of its candidate's agency:
   * the agency whose admin the 'eor' participant emails, and the EOR name printed on the
   * document (in practice the agency's operational name: see the EOR types above). Both come
   * from one backend resolution (ESignService.resolveDocumentEor), so they always name the same
   * EOR. Absent when the document has no EOR, or was sent before these fields.
   */
  eorAgencyId?: string
  eorName?: string
  /**
   * STFFLG-4163: whether the request is signed in order (the backend's effective value). Absent
   * from an older backend, where it reads as sequential, the backend's own default.
   */
  isSequential?: boolean
}

// ═══════════════════════════════════════════════════════════════
// TYPES — Signing Flow (candidate-facing)
// ═══════════════════════════════════════════════════════════════

export interface SigningFieldData {
  id: string
  type: string
  pageIndex: number
  xPct: number
  yPct: number
  widthPct: number
  heightPct: number
  label: string
  required: boolean
  assignee: string
  employeeInfoKey?: string
  checkboxBehavior?: string
  /**
   * SHR-4122: an option may carry its own geometry, in which case it is drawn as a bare tick box at
   * those coordinates rather than as a row in the group's popover. All-or-nothing per group.
   */
  checkboxOptions?: {
    id: string
    label: string
    pageIndex?: number
    xPct?: number
    yPct?: number
    widthPct?: number
    heightPct?: number
  }[]
  /** SHR-4122: false when the uploaded form already prints the question. Absent means draw it. */
  drawQuestion?: boolean
  /** Draw option labels beside each placed tick box. Opt-in; absent means draw nothing. */
  drawOptionLabels?: boolean
  prePopulatedValue?: string
  // Per-participant signing flags (multi-signer). When `editable` is false the
  // field belongs to another signer or was already filled, so it renders
  // read-only. `filledValue` carries the value a prior signer submitted.
  editable?: boolean
  /**
   * STFFLG-4055: `editable: false` means BOTH "belongs to another signer" and "already
   * filled", which is not enough to label a signature box — an untouched one read
   * "Signed". True only when another participant genuinely has a value for this field.
   * Carries the fact, never the mark: a co-signer's signature IMAGE is still withheld
   * (STFFLG-3545), so `filledValue` stays absent for signature fields.
   */
  completedByOtherSigner?: boolean
  filledValue?: string
}

// Returned when a candidate opens a signing request. candidateInfo contains
// auto-populated applicant profile data (name, SSN, DOB, address, etc.)
// for template field pre-fill.
export interface SigningRequestData {
  requestId: string
  fileName: string
  fileUrl: string
  fileId?: string
  participantId: string
  participantOrder: number
  totalParticipants: number
  fields: SigningFieldData[]
  candidateInfo: Record<string, string>
  consentRequired: boolean
  consentGiven: boolean
  status?: 'active' | 'completed' | 'already_completed' | 'cancelled'
  // STFFLG-3868 / SHR-3993: request-level assignment snapshot, FROZEN at send
  // time (never re-resolved). Both are optional and are absent together for
  // unscoped ("By Everyone") sends and for every request that predates
  // per-assignment eSign — absence is the signal to render no context line at
  // all, so legacy documents look byte-for-byte as they did before.
  assignmentLabel?: string
  assignmentSiteName?: string
}

export interface FieldValueEntry {
  fieldId: string
  value: string
}

export interface SigningTaskItem {
  requestId: string
  fileName: string
  sentBy: string
  sentDate: string
  completedDate?: string
  signedDocumentUrl?: string
  // Same request-level assignment snapshot as SignatureRequestDetail, surfaced
  // on the signer's own task list so they can tell two otherwise-identical
  // documents apart. Optional — absent on pre-per-assignment requests.
  assignmentLabel?: string
}

// ═══════════════════════════════════════════════════════════════
// ESIGN API SERVICE
//
// All eSign endpoints are prefixed with /esign on the mspplatform BE.
// Auth is handled automatically by BaseApi (JWT from persisted Redux state).
// Agency context (agencyId) is resolved server-side from the JWT --
// the FE does not need to pass it explicitly.
// ═══════════════════════════════════════════════════════════════

export class EsignApi extends BaseApi {
  /**
   * Read the currently-selected agency ID from persisted Redux state.
   *
   * Multi-agency users switch agencies via the agency-switcher dropdown,
   * which dispatches `updateLoggedInAgencyId`.  We read that value here
   * so every eSign search automatically scopes to the branch the user
   * is currently viewing — consistent with the Candidates Directory.
   */
  private getActiveAgencyId(): string | undefined {
    try {
      const persistedState = localStorage.getItem('persist:root')
      if (persistedState) {
        const parsed = JSON.parse(persistedState)
        if (parsed.loggedInAgencyId) {
          const id = parsed.loggedInAgencyId.replace(/^"|"$/g, '')
          if (id && id !== 'null' && id !== '') return id
        }
      }
    } catch {
      // Silently fall through — backend will use JWT's primary agencyId
    }
    return undefined
  }

  // ─── Files ─────────────────────────────────────────────────

  async getFiles(params?: {
    folderId?: string
    search?: string
    sortBy?: string
    pageNumber?: number
    pageSize?: number
  }): Promise<ESignFilesListResponse> {
    try {
      const response = await this.get<ESignFilesListResponse>('/esign/files', {params})
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching eSign files:', error)
      throw error
    }
  }

  async uploadFile(file: File, folderId?: string): Promise<ESignFileResponse> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (folderId) formData.append('folderId', folderId)

      const response = await this.post<ESignFileResponse>('/esign/files/upload', formData, {
        headers: {'Content-Type': 'multipart/form-data'},
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error uploading eSign file:', error)
      throw error
    }
  }

  async downloadFile(fileId: string): Promise<Blob> {
    try {
      const response = await this.get<Blob>(`/esign/files/${fileId}/download`, {
        responseType: 'blob',
      })
      return new Blob([response.data], {type: response.data?.type || 'application/pdf'})
    } catch (error: unknown) {
      reportCatchError('Error downloading eSign file:', error)
      throw error
    }
  }

  async deleteFile(fileId: string): Promise<void> {
    try {
      await this.delete(`/esign/files/${fileId}`)
    } catch (error: unknown) {
      reportCatchError('Error deleting eSign file:', error)
      throw error
    }
  }

  async renameFile(fileId: string, name: string): Promise<ESignFileResponse> {
    try {
      const response = await this.put<ESignFileResponse>(`/esign/files/${fileId}`, {name})
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error renaming eSign file:', error)
      throw error
    }
  }

  async emailFile(
    fileId: string,
    body: {to: string; cc?: string; subject: string; message: string},
  ): Promise<void> {
    try {
      await this.post(`/esign/files/${fileId}/email`, body)
    } catch (error: unknown) {
      reportCatchError('Error emailing eSign file:', error)
      throw error
    }
  }

  // ─── Folders ───────────────────────────────────────────────

  async getFolders(): Promise<ESignFoldersListResponse> {
    try {
      const response = await this.get<ESignFoldersListResponse>('/esign/folders')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching eSign folders:', error)
      throw error
    }
  }

  async createFolder(name: string): Promise<ESignFolderResponse> {
    try {
      const response = await this.post<ESignFolderResponse>('/esign/folders', {name})
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating eSign folder:', error)
      throw error
    }
  }

  async renameFolder(folderId: string, name: string): Promise<ESignFolderResponse> {
    try {
      const response = await this.put<ESignFolderResponse>(`/esign/folders/${folderId}`, {name})
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error renaming eSign folder:', error)
      throw error
    }
  }

  async deleteFolder(folderId: string): Promise<void> {
    try {
      await this.delete(`/esign/folders/${folderId}`)
    } catch (error: unknown) {
      reportCatchError('Error deleting eSign folder:', error)
      throw error
    }
  }

  // ─── Templates ─────────────────────────────────────────────

  async getTemplateByFileId(fileId: string): Promise<TemplateResponse | null> {
    try {
      const response = await this.get<TemplateResponse>(`/esign/templates/${fileId}`)
      return response.data
    } catch (error: unknown) {
      // 404 means no template exists for this file yet -- not an error
      if (this.isNotFoundError(error)) return null
      reportCatchError('Error fetching eSign template:', error)
      throw error
    }
  }

  async saveTemplate(payload: SaveTemplatePayload): Promise<TemplateResponse> {
    try {
      const response = await this.post<TemplateResponse>('/esign/templates', payload)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error saving eSign template:', error)
      throw error
    }
  }

  async updateTemplate(
    templateId: string,
    payload: SaveTemplatePayload,
  ): Promise<TemplateResponse> {
    try {
      const response = await this.put<TemplateResponse>(`/esign/templates/${templateId}`, payload)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating eSign template:', error)
      throw error
    }
  }

  async deleteTemplate(templateId: string): Promise<void> {
    try {
      await this.delete(`/esign/templates/${templateId}`)
    } catch (error: unknown) {
      reportCatchError('Error deleting eSign template:', error)
      throw error
    }
  }

  // ─── EOR Resolution ────────────────────────────────────────
  // See the TYPES — EOR block above for the rule (STFFLG-4163 D1).

  /**
   * The active agency's own EOR (that agency if isEor, else its parent): the EOR for this
   * agency's own candidates. Every document takes the EOR of ITS candidate's agency, which for a
   * candidate of another agency (a branch) is that agency's EOR; previewEor answers per document.
   *
   * Passes the agency-switcher's selection, exactly as sendSignatureRequest does. Without it the
   * backend resolved from the user's PRIMARY agency, not the one the screen is working in
   * (STFFLG-4163).
   *
   * Throws an Error carrying the server's reason (e.g. 'Cannot resolve EOR: agency "X" is not an
   * EOR and has no parent agency configured'). It used to return null, which left the caller
   * nothing to warn the sender with.
   */
  async resolveEor(
    agencyId: string | undefined = this.getActiveAgencyId(),
  ): Promise<ResolvedEorResponse> {
    try {
      const response = await this.get<ResolvedEorResponse>('/esign/eor/resolve', {
        params: agencyId ? {agencyId} : undefined,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error resolving EOR:', error)
      throw new Error(esignErrorMessage(error, 'The EOR could not be resolved.'))
    }
  }

  /**
   * STFFLG-4163: which EOR each document of a send WOULD get, and who it would email, or why it
   * would be skipped. Takes the send's own recipientIds / audience / agency / signing order and
   * runs the send's own resolution server-side, reading only, so the preview cannot disagree with
   * the send. The backend answers a POST with 201 like any other.
   */
  async previewEor(body: EorPreviewRequestBody): Promise<EorPreviewResponse> {
    try {
      const agencyId = body.agencyId ?? this.getActiveAgencyId()
      const payload = agencyId ? {...body, agencyId} : body
      const response = await this.post<EorPreviewResponse>('/esign/eor/preview', payload)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error previewing the EOR signer:', error)
      throw new Error(esignErrorMessage(error, 'The EOR signer could not be previewed.'))
    }
  }

  // ─── Candidates & Sites ────────────────────────────────────
  // These power the recipient selection UI when creating a
  // signature request. The BE resolves agencyId from the JWT
  // to scope results to the correct agency.

  // Typeahead search -- returns up to 50 results. User narrows
  // results by typing more characters (e.g. "john" -> "john sm").
  /**
   * Typeahead search for candidates scoped to the active agency.
   *
   * Passes `agencyId` from the agency-switcher so branch-agency
   * candidates are visible — fixes SHR-3792 where multi-agency users
   * could not find branch candidates in the eSign search.
   */
  async searchCandidates(query: string): Promise<ESignCandidateResult[]> {
    try {
      const agencyId = this.getActiveAgencyId()
      const response = await this.get<{candidates: ESignCandidateResult[]}>(
        '/esign/candidates/search',
        {params: {q: query, ...(agencyId && {agencyId})}},
      )
      return response.data.candidates ?? []
    } catch (error: unknown) {
      reportCatchError('Error searching eSign candidates:', error)
      throw error
    }
  }

  async searchPeople(query: string): Promise<ESignPersonResult[]> {
    try {
      const agencyId = this.getActiveAgencyId()
      const response = await this.get<{results: ESignPersonResult[]}>('/esign/people/search', {
        params: {q: query, ...(agencyId && {agencyId})},
      })
      return response.data.results ?? []
    } catch (error: unknown) {
      /**
       * Degrade to candidates, but SAY SO.
       *
       * This was a bare catch. A 403 from agency resolution, a 404 from an older backend and a
       * 500 from the aggregate all came back as a candidates-only list, indistinguishable from
       * "nobody matched" — so a broken agency-user search read as missing data, which is exactly
       * how STFFLG's agency-signer bug presented. The fallback is still worth having; it just
       * must not be silent.
       */
      reportCatchError('Error searching eSign people (falling back to candidates):', error)
      return this.searchCandidatesFallback(query)
    }
  }

  private async searchCandidatesFallback(query: string): Promise<ESignPersonResult[]> {
    try {
      const candidates = await this.searchCandidates(query)
      return candidates.map((c) => ({...c, personType: 'candidate' as PersonType}))
    } catch {
      return []
    }
  }

  /**
   * Fetch all active (non-terminated) candidates for the current agency.
   * Passes `agencyId` for branch-aware scoping.
   */
  async getAllActiveCandidates(): Promise<AllCandidatesResponse> {
    try {
      const agencyId = this.getActiveAgencyId()
      const response = await this.get<AllCandidatesResponse>('/esign/candidates/active', {
        params: agencyId ? {agencyId} : undefined,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching active candidates:', error)
      throw error
    }
  }

  async getSites(): Promise<ESignSiteResult[]> {
    try {
      const agencyId = this.getActiveAgencyId()
      const response = await this.get<{sites: ESignSiteResult[]}>('/esign/sites', {
        params: agencyId ? {agencyId} : undefined,
      })
      return response.data.sites ?? []
    } catch (error: unknown) {
      reportCatchError('Error fetching eSign sites:', error)
      throw error
    }
  }

  async getCandidatesBySite(siteId: string): Promise<ESignCandidateResult[]> {
    try {
      const agencyId = this.getActiveAgencyId()
      const response = await this.get<{candidates: ESignCandidateResult[]}>(
        `/esign/sites/${siteId}/candidates`,
        {params: agencyId ? {agencyId} : undefined},
      )
      return response.data.candidates ?? []
    } catch (error: unknown) {
      reportCatchError('Error fetching candidates by site:', error)
      throw error
    }
  }

  async getDepartmentCandidateCounts(
    departmentIds: string[],
    jobPositions?: string[],
  ): Promise<Record<string, number>> {
    try {
      const params = new URLSearchParams()
      params.set('departmentIds', departmentIds.join(','))
      if (jobPositions?.length) {
        params.set('jobPositions', jobPositions.join(','))
      }
      const agencyId = this.getActiveAgencyId()
      if (agencyId) params.set('agencyId', agencyId)
      const response = await this.get<{counts: Record<string, number>}>(
        `/esign/departments/counts?${params.toString()}`,
      )
      return response.data.counts ?? {}
    } catch (error: unknown) {
      reportCatchError('Error fetching department candidate counts:', error)
      return {}
    }
  }

  // ─── Signature Requests (agency-side management) ───────────

  async sendSignatureRequest(body: SendSignatureRequestBody): Promise<{
    id: string
    /** SHR-4032: how many documents the send actually created. */
    totalCreated?: number
    /**
     * SHR-4032: documents not created, one entry each, as '<First Last — EA000001 - Title>
     * (<why>)'. The why is 'no email address for: <slots>' or, since STFFLG-4163, 'no EOR
     * signer: <reason naming the candidate's agency>'. See describeSkippedRecipients.
     */
    skippedRecipients?: string[]
  }> {
    try {
      // Scope the send to the same active branch agency used for candidate
      // search so multi-agency users hit the correct agency context.
      const agencyId = body.agencyId ?? this.getActiveAgencyId()
      const payload = agencyId ? {...body, agencyId} : body
      const response = await this.post<{id: string}>('/esign/requests', payload)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error sending signature request:', error)
      throw error
    }
  }

  async getSignatureRequests(params?: {
    status?: SignatureRequestStatus
    search?: string
    page?: number
    pageSize?: number
  }): Promise<SignatureRequestsListResponse> {
    try {
      const response = await this.get<SignatureRequestsListResponse>('/esign/requests', {params})
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching signature requests:', error)
      throw error
    }
  }

  async getSignatureRequestDetail(requestId: string): Promise<SignatureRequestDetail> {
    try {
      const response = await this.get<SignatureRequestDetail>(`/esign/requests/${requestId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching signature request detail:', error)
      throw error
    }
  }

  async sendReminder(requestId: string, participantId: string): Promise<void> {
    try {
      await this.post(`/esign/requests/${requestId}/remind`, {participantId})
    } catch (error: unknown) {
      reportCatchError('Error sending signing reminder:', error)
      throw error
    }
  }

  // ─── Signing Flow (candidate-facing) ───────────────────────
  // These endpoints are used by the candidate (applicant) who
  // is signing the document -- viewing the PDF, giving ESIGN Act
  // consent, filling in fields, and completing the signing.

  async getSigningData(requestId: string): Promise<SigningRequestData> {
    try {
      const response = await this.get<SigningRequestData>(`/esign/signing/${requestId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching signing data:', error)
      throw error
    }
  }

  async submitConsent(requestId: string): Promise<void> {
    try {
      await this.post(`/esign/signing/${requestId}/consent`)
    } catch (error: unknown) {
      reportCatchError('Error submitting signing consent:', error)
      throw error
    }
  }

  async saveFieldValues(requestId: string, fieldValues: FieldValueEntry[]): Promise<void> {
    try {
      await this.put(`/esign/signing/${requestId}/fields`, {fieldValues})
    } catch (error: unknown) {
      reportCatchError('Error saving field values:', error)
      throw error
    }
  }

  // Finalizes signing -- saves field values and marks participant as signed.
  // After completion, the BE notifies the sender and advances to the next participant.
  async completeSigning(
    requestId: string,
    fieldValues: FieldValueEntry[],
  ): Promise<{signedDocumentUrl?: string}> {
    try {
      const response = await this.post<{signedDocumentUrl?: string}>(
        `/esign/signing/${requestId}/complete`,
        {fieldValues},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error completing signing:', error)
      throw error
    }
  }

  async getSigningTasks(status?: 'pending' | 'completed'): Promise<SigningTaskItem[]> {
    try {
      const params = status ? {status} : {}
      const response = await this.get<{requests: SigningTaskItem[]}>('/esign/signing/tasks', {
        params,
      })
      return response.data.requests ?? []
    } catch (error: unknown) {
      reportCatchError('Error fetching signing tasks:', error)
      throw error
    }
  }

  async downloadSigningFile(url: string): Promise<Blob> {
    try {
      const response = await this.get<Blob>(url, {responseType: 'blob'})
      return new Blob([response.data], {type: response.data?.type || 'application/pdf'})
    } catch (error: unknown) {
      reportCatchError('Error downloading signing file:', error)
      throw error
    }
  }

  // ─── Signed Document Downloads ─────────────────────────────

  async downloadSignedDocument(requestId: string, participantId: string): Promise<Blob> {
    try {
      const response = await this.get<Blob>(
        `/esign/requests/${requestId}/download/${participantId}`,
        {responseType: 'blob'},
      )
      return new Blob([response.data], {type: response.data?.type || 'application/pdf'})
    } catch (error: unknown) {
      reportCatchError('Error downloading signed document:', error)
      throw error
    }
  }

  async downloadSignedDocumentsBatch(requestId: string, participantIds: string[]): Promise<Blob> {
    try {
      const response = await this.post<Blob>(
        `/esign/requests/${requestId}/download-batch`,
        {participantIds},
        {responseType: 'blob'},
      )
      return new Blob([response.data], {type: 'application/zip'})
    } catch (error: unknown) {
      reportCatchError('Error downloading signed documents batch:', error)
      throw error
    }
  }

  // ─── Convenience ───────────────────────────────────────────

  private isNotFoundError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {response?: {status?: number}}
      return axiosError.response?.status === 404
    }
    return false
  }
}

// Singleton instance -- import this in components
export const esignApi = new EsignApi()

// ─── Browser Download Helper ─────────────────────────────────
// Triggers a browser file download from a Blob (used after downloadFile)
export const triggerFileDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
